import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Volume2, Clock, Award, CheckCircle, Users, Calendar, Headphones, Landmark, Crown, AudioLines, Disc, Martini, Loader2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';
import WeddingDiamond from '../components/WeddingDiamond';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ApexIcon = ({ className = "w-6 h-6", variant = "light" }) => (
  <img 
    src={variant === "dark" 
      ? "/AudioApex_icon_inverse_transparent.svg"
      : "/AudioApex_icon_transparent.svg"
    }
    alt="Audio Apex"
    className={className}
  />
);

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products`);
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        const order = ['9', '1', '2'];
        const filtered = order
          .map(id => data.find(p => p.id === id))
          .filter(Boolean);
        setFeaturedProducts(filtered);
      } catch {
        setFeaturedProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero - full viewport image with content on the floor */}
      <section className="relative overflow-hidden bg-slate-900 min-h-[600px]" style={{height: '100dvh'}}>
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Desktop image */}
          <img
            src="https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/hijjb95i_PHOTO-2026-06-05-14-47-26.jpg"
            alt="Warehouse venue with Pequod Acoustics Vesper speakers and DJ setup"
            className="hidden md:block w-full h-full object-cover animate-slow-zoom"
            style={{objectPosition: 'center bottom'}}
          />
          {/* Mobile image - oversized height allows cropping the ceiling */}
          <div
            className="md:hidden absolute inset-0 bg-no-repeat animate-slow-zoom"
            style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/hijjb95i_PHOTO-2026-06-05-14-47-26.jpg)',
              backgroundSize: 'auto 140%',
              backgroundPosition: '55% 80%'
            }}
          />
          {/* Top gradient - blends fixed header into image */}
          <div className="absolute inset-x-0 top-0 h-28 z-[1]" style={{background: 'linear-gradient(to bottom, rgba(15,23,42,0.7), transparent)'}} />
          {/* Bottom gradient - strong dark sweep for text legibility + badge strip */}
          <div className="absolute inset-x-0 bottom-0 h-[55%] sm:h-[40%] z-[1]" style={{background: 'linear-gradient(to top, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.7) 40%, transparent 100%)'}} />
        </div>

        {/* Content - text and buttons */}
        <div className="absolute bottom-0 left-0 right-0 z-20 px-4 sm:px-6 lg:px-8 pb-[130px] sm:pb-32 md:pb-16">
          <div className="max-w-[280px] sm:max-w-xl mx-auto" style={{paddingRight: '8px'}}>
            <p className="text-base sm:text-xl md:text-2xl text-white leading-snug sm:leading-snug mb-0 md:mb-1 font-medium drop-shadow-lg text-center">
              Sculptural Sound Systems<br />for London&apos;s finest events
            </p>
            <div className="flex items-center justify-center gap-2 sm:gap-3 mt-10 md:mt-4">
              <Link to="/packages">
                <button className="flex items-center justify-center bg-[#e2e8f0] hover:bg-[#d1d5db] rounded-full px-3 py-1 sm:px-3.5 sm:py-1.5 text-slate-900 font-semibold text-[10px] sm:text-xs shadow-xl hover:shadow-2xl transition-all duration-200 whitespace-nowrap hover:scale-105" data-testid="hero-browse-packages-btn">
                  Browse Packages
                  <ArrowRight className="w-3 h-3 ml-1" />
                </button>
              </Link>
              <Link to="/contact">
                <button className="flex items-center justify-center bg-[#e2e8f0] hover:bg-[#d1d5db] rounded-full px-3 py-1 sm:px-3.5 sm:py-1.5 text-slate-900 font-semibold text-[10px] sm:text-xs shadow-xl hover:shadow-2xl transition-all duration-200 whitespace-nowrap hover:scale-105" data-testid="hero-get-quote-btn">
                  Request Quote
                  <ArrowRight className="w-3 h-3 ml-1" />
                </button>
              </Link>
            </div>
          </div>
        </div>
        {/* Mobile trust indicators - inside the dark gradient strip */}
        <div className="sm:hidden absolute bottom-[45px] left-0 right-0 z-20 px-4">
          <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 justify-items-center max-w-[280px] mx-auto" style={{paddingRight: '8px'}}>
            {[
              { icon: Clock, text: 'Last-Minute Hire' },
              { icon: Calendar, text: 'Flexible Rentals' },
              { icon: Users, text: '20-2,000 Guests' },
              { icon: Zap, text: 'Premium Sound' }
            ].map((feature) => (
              <div key={feature.text} className="flex items-center gap-1.5 px-2.5 py-1 border border-slate-500/60 rounded-lg bg-slate-900/30 backdrop-blur-sm">
                <feature.icon className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
                <span className="font-medium text-[11px] text-white/80 whitespace-nowrap">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile safe-area buffer - covers the gap behind Safari toolbar so the next section doesn't peek */}
      <div className="sm:hidden bg-slate-900 h-[120px] -mb-[120px] relative z-0" />

      {/* Trust indicators band - desktop only separate section */}
      <section className="hidden sm:block bg-slate-900 border-b-2 border-slate-400">
        <div className="flex flex-wrap justify-center items-center gap-x-3 md:gap-x-4 py-1.5 px-4 sm:px-6 lg:px-8">
          {[
            { icon: Clock, text: 'Last-Minute Hire' },
            { icon: Calendar, text: 'Flexible Rentals' },
            { icon: Users, text: '20-2,000 Guests' },
            { icon: Zap, text: 'Pioneer & Pequod' }
          ].map((feature, idx) => (
            <div key={feature.text || idx} className="flex items-center gap-1.5 px-2.5 py-1 border border-slate-500 rounded-lg">
              <feature.icon className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
              <span className="font-medium text-xs text-white/80 whitespace-nowrap">{feature.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Sound Systems Section - Silver */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#e2e8f0] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          {/* AA watermark - right side */}
          <img
            src="/AudioApex_icon_inverse_transparent.svg"
            alt=""
            className="hidden lg:block absolute right-0 top-0 w-72 xl:w-96 h-auto opacity-[0.04] pointer-events-none"
          />
          {/* Copy */}
          <div className="max-w-3xl mb-14">
            <Badge className="bg-slate-800 text-white border-0 px-4 py-1.5 text-xs font-semibold shadow-md mb-5">
              OUR SOUND SYSTEMS
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 leading-tight heading-font mb-5">
              Designed to Be Seen, Engineered to Be Heard
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-6">
              Audiophile-grade sound systems you&apos;ll want on display. Speakers designed to look as good as they sound, no stack of black boxes in the corner. The kind of detail your guests notice.
            </p>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 mb-6">
              {[
                'Sculptural, design-led speakers that become part of the room',
                'True hi-fi sound engineered for critical, immersive listening',
                'Scales from 20-person rooms to 2,000-guest venues',
                'Trusted by London\'s leading venues and event producers'
              ].map((feature, idx) => (
                <li key={feature} className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span className="text-slate-700 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            <Link to="/packages">
              <Button size="lg" className="bg-slate-800 hover:bg-slate-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-6 py-5">
                View All Packages
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Pequod Acoustics Tiers */}
          <div className="mb-6">
            <p className="text-xs font-semibold tracking-widest text-slate-500 uppercase mb-1">Pequod Acoustics</p>
            <p className="text-sm text-slate-600">Hand-crafted Italian speaker systems, configured and tuned for your venue.</p>
          </div>

          {/* Package Cards - 3 column grid */}
          <div className="grid md:grid-cols-3 gap-5">
            <Link to="/packages#package-1" className="group">
              <div className="relative overflow-hidden rounded-xl border border-slate-300 hover:border-slate-500 transition-all duration-500 shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/i0njwxs0_The%20intimate.jpeg"
                  alt="The Intimate - Vesper Speaker System"
                  className="w-full aspect-square object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent flex items-end p-5">
                  <div className="w-full">
                    <Badge className="mb-2 bg-purple-600 text-white border-0 text-xs">Intimate</Badge>
                    <p className="text-white font-semibold text-base mb-1 heading-font">The Intimate</p>
                    <div className="flex justify-between items-center">
                      <p className="text-gray-300 text-xs">20-100 guests</p>
                      <p className="text-white font-bold text-sm">From £650</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
            <Link to="/packages#package-3" className="group">
              <div className="relative overflow-hidden rounded-xl border border-slate-300 hover:border-slate-500 transition-all duration-500 shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/93x0t34z_The%20signature.jpeg"
                  alt="The Signature - Kona Speaker System"
                  className="w-full aspect-square object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent flex items-end p-5">
                  <div className="w-full">
                    <Badge className="mb-2 bg-blue-600 text-white border-0 text-xs">Professional</Badge>
                    <p className="text-white font-semibold text-base mb-1 heading-font">The Signature</p>
                    <div className="flex justify-between items-center">
                      <p className="text-gray-300 text-xs">100-300 guests</p>
                      <p className="text-white font-bold text-sm">From £1,500</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
            <Link to="/packages#package-5" className="group">
              <div className="relative overflow-hidden rounded-xl border border-slate-300 hover:border-slate-500 transition-all duration-500 shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_9adb6eda-13f9-4f44-b8c5-7c04267d6a44/artifacts/9f1e739l_Package%205.jpeg"
                  alt="Flagship Polyphemus Experience"
                  className="w-full aspect-square object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent flex items-end p-5">
                  <div className="w-full">
                    <Badge className="mb-2 bg-amber-600 text-white border-0 text-xs">Flagship</Badge>
                    <p className="text-white font-semibold text-base mb-1 heading-font">The Flagship</p>
                    <div className="flex justify-between items-center">
                      <p className="text-gray-300 text-xs">300-600+ guests</p>
                      <p className="text-white font-bold text-sm">From £4,000</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Where Audio Apex Shines - Dark */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden" data-testid="settings-section">
        {/* Subtle watermark */}
        <img
          src="/AudioApex_icon_transparent.svg"
          alt=""
          className="hidden lg:block absolute left-0 bottom-0 w-64 xl:w-80 h-auto opacity-[0.04] pointer-events-none"
        />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-[#e2e8f0] text-slate-800 border-0 px-4 py-1.5 text-xs font-semibold shadow-md">
              VERSATILE BY DESIGN
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 heading-font">Where Audio Apex Shines</h2>
            <p className="text-base text-gray-400 max-w-2xl mx-auto">
              Premium HiFi sound for every setting
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Landmark, title: 'Art Galleries & Exhibitions', desc: 'Immersive soundscapes that move with the space', img: 'https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/sh6vsk4o_Screenshot_2026-06-10_at_20_01_20.png' },
              { icon: Crown, title: 'Brand Activations & Launches', desc: 'Sound and design as one statement', img: 'https://customer-assets.emergentagent.com/job_bed0a868-104c-4e19-8a0f-6978ab192514/artifacts/gwoq7tb1_pequod_acoustics_5.webp' },
              { icon: AudioLines, title: 'Corporate & Speaking Events', desc: 'Pristine clarity for voice and atmosphere alike', img: 'https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/rn6t5hcz_corporate%20image.jpeg' },
              { icon: Disc, title: 'Listening Bars & Private Clubs', desc: 'Audiophile sound for the hi-fi-led experience', img: 'https://customer-assets.emergentagent.com/job_dj-pequod-london/artifacts/58nojhvh_CM_Vip_05.jpeg.webp' },
              { icon: Martini, title: 'Private Parties', desc: 'Intimate, high-fidelity sound, beautifully presented', img: 'https://customer-assets.emergentagent.com/job_bed0a868-104c-4e19-8a0f-6978ab192514/artifacts/njz86end_19Pequod_Acoustics_kona.webp' },
              { icon: WeddingDiamond, title: 'Weddings', desc: 'From ceremony ambience to a dancefloor that lasts', img: 'https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/9at5b63v_NAQ%20the%20Box-19850.jpg' },
            ].map((item) => (
              <div
                key={item.title}
                className="group relative overflow-hidden rounded-xl border border-slate-300 hover:border-slate-500 transition-all duration-500 aspect-[4/3]"
              >
                <img
                  src={item.img}
                  alt={item.title}
                  className={`absolute inset-0 w-full h-full transform group-hover:scale-105 transition-transform duration-700 ${item.contain ? 'object-contain bg-slate-900' : 'object-cover'}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-5">
                  <h3 className="text-base font-bold text-white mb-1 heading-font">{item.title}</h3>
                  <p className="text-sm text-gray-300 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Silver Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#e2e8f0] relative overflow-hidden">
        {/* Subtle watermark */}
        <img
          src="/AudioApex_icon_inverse_transparent.svg"
          alt=""
          className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 w-56 xl:w-72 h-auto opacity-[0.04] pointer-events-none"
        />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-slate-800 text-white border-0 px-4 py-1.5 text-xs font-semibold shadow-md">
              WHY AUDIO APEX
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3 heading-font">The Apex Advantage</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Serving London and surrounding areas with unmatched service and premium equipment
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
            {
              icon: Clock,
              title: 'Flexible Rental Terms',
              description: 'Last-minute emergency hires to long-term contracts, we adapt to your schedule with 24/7 availability and fast quotes.'
            },
            {
              icon: Award,
              title: 'Premium Equipment',
              description: 'Brand-new Pioneer CDJ-3000, DJM-V10, and exclusive Pequod Acoustics systems trusted by London\'s leading venues.'
            },
            {
              icon: Headphones,
              title: 'Expert Support',
              description: 'London-based team providing consultation, delivery, on-site setup, and 24/7 technical support throughout your event.'
            }].
            map((feature, idx) =>
            <Card key={feature.title} className="border-slate-300 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 bg-white shadow-lg">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mb-5 shadow-lg">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-3 heading-font">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Gallery Section - Dark */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden">
        {/* Subtle watermark */}
        <img
          src="/AudioApex_icon_transparent.svg"
          alt=""
          className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-64 xl:w-80 h-auto opacity-[0.04] pointer-events-none"
        />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-[#e2e8f0] text-slate-800 border-0 px-4 py-1.5 text-xs font-semibold shadow-md">
              GALLERY
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 heading-font">See Us In Action</h2>
            <p className="text-base text-gray-400 max-w-2xl mx-auto">
              Our systems powering venues and events across London
            </p>
          </div>
          
          {(() => {
            const galleryImages = [
              {
                url: 'https://customer-assets.emergentagent.com/job_dj-pequod-london/artifacts/jrc2j6yi_pequod_villa_buonaccorsi-5.webp',
                alt: 'Historic villa courtyard setup'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_bed0a868-104c-4e19-8a0f-6978ab192514/artifacts/2nqtxwat_WhatsApp%20Image%202026-02-19%20at%2021.39.52%20%282%29.jpeg',
                alt: 'Racing circuit red Pequod installation'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/4y3coaik_NAQ%20the%20Box-19566.jpg',
                alt: 'NAQ The Box DJ booth with crowd and Vesper speakers'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_dj-pequod-london/artifacts/ula4yu7h_CM_Vip_01.jpeg.webp',
                alt: 'VIP lounge with Vesper speakers'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_bed0a868-104c-4e19-8a0f-6978ab192514/artifacts/vnujwnqo_def%2Bday%2B51%2Bpb-13.webp',
                alt: 'Seaside venue with Pequod speakers'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_bed0a868-104c-4e19-8a0f-6978ab192514/artifacts/njz86end_19Pequod_Acoustics_kona.webp',
                alt: 'DJs performing with red Vesper speakers'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_bed0a868-104c-4e19-8a0f-6978ab192514/artifacts/gwoq7tb1_pequod_acoustics_5.webp',
                alt: 'Green Pequod speakers in luxury interior'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/80vio53g_NAQ%20the%20Box-19696.jpg',
                alt: 'People dancing next to Vesper speakers with neon lights'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_dj-pequod-london/artifacts/3rbj6n10_Screenshot%2B%28123%29.webp',
                alt: 'DJ setup with mountains backdrop'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/1eu18855_NAQ%20the%20Box-19334.jpg',
                alt: 'Female DJ performing with red Vesper speaker'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_dj-pequod-london/artifacts/aacv2j60_Riverhouse_07262019_edits-145.webp',
                alt: 'The Riverhouse performance'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_bed0a868-104c-4e19-8a0f-6978ab192514/artifacts/504gghel_WhatsApp%20Image%202026-02-19%20at%2021.39.52%20%284%29.jpeg',
                alt: 'Ancient amphitheatre DJ setup'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_bed0a868-104c-4e19-8a0f-6978ab192514/artifacts/3nlvm9au_DSC06156.webp',
                alt: 'Concert stage with colorful Pequod speakers'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/1mdhvwsj_NAQ%20the%20Box-19908.jpg',
                alt: 'DJ and percussionist performing with red Vesper speaker'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_dj-pequod-london/artifacts/og747k5h_85150673_867729227004251_4651416450190278656_n.webp',
                alt: 'Blue lit DJ booth with NASA branding'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_e978e63f-237e-4264-85e1-a0b27cb0ed63/artifacts/obx0xz51_%26me%20photo%20for%20gallery.jpeg',
                alt: 'Outdoor sunset party with Kona speakers and DJ'
              }
            ];
            return (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {galleryImages.map((image, idx) => (
                    <div
                      key={image.url}
                      className="relative group overflow-hidden rounded-xl border border-slate-700 aspect-[4/3] cursor-pointer"
                      onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                      data-testid={`gallery-image-${idx}`}
                    >
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  ))}
                </div>

                {lightboxOpen && (
                  <div
                    className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
                    onClick={() => setLightboxOpen(false)}
                    data-testid="gallery-lightbox"
                  >
                    <button
                      className="absolute top-4 right-4 z-50 text-white/70 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      onClick={() => setLightboxOpen(false)}
                      data-testid="lightbox-close-btn"
                    >
                      <X className="w-6 h-6" />
                    </button>
                    <button
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white/70 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + galleryImages.length) % galleryImages.length); }}
                      data-testid="lightbox-prev-btn"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white/70 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % galleryImages.length); }}
                      data-testid="lightbox-next-btn"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                    <img
                      src={galleryImages[lightboxIndex].url}
                      alt={galleryImages[lightboxIndex].alt}
                      className="max-h-[90vh] max-w-[90vw] object-contain"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
                      {lightboxIndex + 1} / {galleryImages.length}
                    </p>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* CTA Section - Silver */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#e2e8f0] relative overflow-hidden">
        {/* Subtle watermark */}
        <img
          src="/AudioApex_icon_inverse_transparent.svg"
          alt=""
          className="hidden lg:block absolute left-8 top-1/2 -translate-y-1/2 w-48 xl:w-64 h-auto opacity-[0.04] pointer-events-none"
        />
        <div className="max-w-4xl mx-auto text-center relative">
          {/* Decorative Icon */}
          <div className="flex justify-center mb-6">
            <ApexIcon className="w-12 h-12 opacity-40" variant="dark" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-5 heading-font">
            Ready to Transform Your Event?
          </h2>
          <p className="text-base text-slate-600 mb-8 leading-relaxed max-w-2xl mx-auto">
            Whether you need equipment tonight or for a 2,000-guest festival next month, our team is ready. 
            Get your quote for events across London and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-slate-800 hover:bg-slate-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-6 font-semibold">
                Get a Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="border-2 border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white px-8 py-6 font-semibold transition-all duration-300">
                Browse Equipment
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;